import React, {
  MutableRefObject,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  useTable,
  useFlexLayout,
  useRowSelect,
  useSortBy,
  useColumnOrder,
  useExpanded,
  PluginHook,
} from 'react-table';

import {
  TableBulkActions,
  TableColumnSettings,
  TableLayout,
  TableRowActions,
} from './components';
import { Flex } from '@chakra-ui/react';
import { Pagination } from '../Pagination';
import { useCustomRowSelectColumn } from './components/useSelectedRowColumn';
import { useRowSelectColumn } from '@lineup-lite/hooks';
import { LoadMore } from '../loadmore';
import { useSelectAllColumn } from './components/useSelectAllColumn';
import { useBulkOperationsOptional } from '../BulkOperationQueue';

type Pagination = { limit?: number; total?: number; [k: string]: unknown };
type PageInfo = { number?: number; [k: string]: unknown };

interface TableProps {
  tableId?: string;
  columns?: any[];
  data?: any[];
  pagination?: Pagination;
  applyPage?: (_args: any[]) => void;
  page?: PageInfo;
  bulkActionMenuItems?: any[];
  rowActionMenuItems?: any[];
  isColumnLevelSorting?: boolean;
  rowActionProps?: Record<string, any>;
  paginationType?: string;
  enableColumnConfig?: boolean;
  tableContainerStyles?: Record<string, any>;
  rowActionMenuStyle?: Record<string, any>;
  tableLayoutStyles?: Record<string, any>;
  paginationLimit?: number;
  skipPagination?: boolean;
  layoutStyles?: Record<string, any>;
  layoutTableStyles?: Record<string, any>;
  totalKey?: string;
  toResetValue?: boolean;
  type?: string;
  testID?: string;
  enableLoadMore?: boolean;
  layoutScrollContainerStyles?: Record<string, any> | string;
  customRowhook?: boolean;
  selectableRows?: any;
  disableSpecificRowFn?: any;
  toHide?: boolean;
  isLoadingMore?: boolean;
  tableRef?: any;
  addDefaultColumnAtTop?: boolean;
  tableLayoutWrapperStyles?: Record<string, any>;
  enableFooter?: boolean;
  enableExpanded?: boolean;
  getTableState?: (_state?: any) => void;
  tableColStyles?: Record<string, any>;
  onTableColDataChange?: (_args: any[]) => void;
  initialTableColData?: any;
  skipLocalStorage?: boolean;
  defaultSortBy?: any;
  rowStyles?: Record<string, any>;
  enableNthSelection?: boolean;
  enableLimitSelection?: boolean;
  applyLimit?: any;
  isDispatch?: boolean;
  /** Ref whose .current will be set to a function that clears row selection (call when bulk operation completes) */
  clearSelectionRef?: MutableRefObject<(() => void) | null> | null;
  manualSortBy?: boolean;
}

const TableComponent = ({
  tableId = undefined,
  columns = [],
  data = [],
  pagination = {},
  applyPage = undefined,
  page = {},
  bulkActionMenuItems = [],
  rowActionMenuItems = undefined,
  isColumnLevelSorting = false,
  rowActionProps = { width: 50 },
  paginationType = 'meta',
  enableColumnConfig = false,
  tableContainerStyles = {},
  rowActionMenuStyle = {},
  tableLayoutStyles = {},
  paginationLimit = 20,
  skipPagination = false,
  layoutStyles = {},
  layoutTableStyles = {},
  totalKey = 'total',
  toResetValue = false,
  type = 'inPage',
  testID = 'table',
  enableLoadMore = false,
  layoutScrollContainerStyles = {},
  customRowhook = false,
  selectableRows = null,
  disableSpecificRowFn = null,
  toHide = false,
  isLoadingMore = false,
  tableRef = null,
  addDefaultColumnAtTop = false,
  tableLayoutWrapperStyles = {},
  enableFooter = false,
  enableExpanded = false,
  getTableState = () => {},
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  defaultSortBy = undefined,
  rowStyles = {},
  enableNthSelection = true,
  enableLimitSelection = false,
  applyLimit = null,
  isDispatch = true,
  /** Ref whose .current will be set to a function that clears row selection (call when bulk operation completes) */
  clearSelectionRef = null,
  manualSortBy = false,
}: TableProps) => {
  const enableRowAction = rowActionMenuItems?.length > 0;
  const isRowSelectable = bulkActionMenuItems?.length > 0;

  const storedSettings = skipLocalStorage
    ? {}
    : localStorage.getItem(`tableColSettings_${tableId}`)
      ? JSON.parse(localStorage.getItem(`tableColSettings_${tableId}`))
      : {};
  const parsedSettings = storedSettings;

  // Priority: initialTableColData > localStorage (if not skipped) > defaults
  const effectiveSettings =
    initialTableColData || (!skipLocalStorage ? parsedSettings : {}) || {};
  const { visibility = {}, order = [] } = effectiveSettings;

  const hiddenColumns = useMemo(
    () => Object.keys(visibility).filter((key) => !visibility[key]) || [],
    [visibility]
  );

  const [selectionState, setSelectionState] = useState('inPage');
  const [unSelectedHash, setUnSelectedHash] = useState<
    Record<string | number, any[]>
  >({});
  const [prevPage, setPrevPage] = useState<number | null>(null);

  const tableColumns = useMemo(() => {
    const columnsCopy = [...columns];

    if (enableRowAction) {
      const tableRowAction = {
        Header: () => <></>,
        accessor: 'table_row_actions',
        Cell: (props) => (
          <TableRowActions
            {...props}
            actionItems={rowActionMenuItems}
            className="tableRowActions"
            rowActionMenuStyle={rowActionMenuStyle}
            row={{ ...props.row, index: props.row.index }}
          />
        ),
        isAllowedToExternalUsers: true,
        ...rowActionProps,
      };

      columnsCopy.push(tableRowAction);
    }

    return columnsCopy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, enableRowAction]);

  // Memoize stateReducer to prevent react-table from reinitializing.
  // When user toggles and sort would clear (sortBy []), cycle to opposite direction instead of forcing defaultSortBy so asc/desc both work.
  const stateReducer = useCallback(
    (newState, action, prevState) => {
      if (
        isColumnLevelSorting &&
        defaultSortBy &&
        action.type === 'toggleSortBy' &&
        newState.sortBy.length === 0 &&
        prevState.sortBy.length > 0
      ) {
        const prev = prevState.sortBy[0];
        return {
          ...newState,
          sortBy: [{ id: prev.id, desc: !prev.desc }],
        };
      }
      return newState;
    },
    [isColumnLevelSorting, defaultSortBy]
  );

  // Memoize initialState to prevent unnecessary recalculations
  const initialState = useMemo(
    () => ({
      hiddenColumns,
      ...(order.length > 0 && { columnOrder: order }),
      ...(defaultSortBy && { sortBy: defaultSortBy }),
    }),
    [hiddenColumns, order, defaultSortBy]
  );

  // Memoize tableParams to prevent react-table from reinitializing on every render
  const tableParams = useMemo(
    () => ({
      columns: tableColumns,
      data: data,
      initialState,
      stateReducer,
      ...(manualSortBy && { manualSortBy: true }),
    }),
    [tableColumns, data, initialState, stateReducer, manualSortBy]
  );

  // Memoize tableHooks to prevent react-table from reinitializing
  const tableHooks = useMemo<PluginHook<any>[]>(() => {
    const hooks: PluginHook<any>[] = [useFlexLayout, useColumnOrder];
    if (isColumnLevelSorting) {
      hooks.push(useSortBy);
    }
    return hooks;
  }, [isColumnLevelSorting]);

  const handleSelectState = useCallback((state) => {
    setSelectionState(state);
  }, []);

  const resetValue = useCallback(() => {
    setUnSelectedHash({});
    setSelectionState('inPage');
  }, []);

  useEffect(() => {
    if (toResetValue) {
      resetValue();
    }
  }, [toResetValue, resetValue]);

  // Memoize the complete hooks array including conditional hooks
  const allTableHooks = useMemo<PluginHook<any>[]>(() => {
    const hooks: PluginHook<any>[] = [...tableHooks];

    if (enableExpanded) {
      hooks.push(useExpanded);
    }

    if (isRowSelectable) {
      hooks.push(useRowSelect);

      if (customRowhook) {
        hooks.push(useCustomRowSelectColumn);
      } else if (type === 'selectAllPage') {
        hooks.push(useSelectAllColumn);
      } else {
        hooks.push(useRowSelectColumn);
      }
    }

    return hooks;
  }, [tableHooks, enableExpanded, isRowSelectable, customRowhook, type]);

  // Memoize the final tableParams with all conditional properties
  const finalTableParams = useMemo<Record<string, any>>(() => {
    const params: Record<string, any> = { ...tableParams };

    if (enableExpanded) {
      params.initialState = {
        ...params.initialState,
        expanded: { 0: true },
      };
    }

    if (isRowSelectable && customRowhook) {
      params.selectableRows = selectableRows;
      params.disableSpecificRowFn = disableSpecificRowFn;
    } else if (isRowSelectable && type === 'selectAllPage') {
      params.selectAllColumnOptions = {
        handleSelectionState: handleSelectState,
        resetValue: resetValue,
        type,
        pageLimit: pagination?.limit,
        totalCount: pagination?.total,
      };
    }

    return params;
  }, [
    tableParams,
    enableExpanded,
    isRowSelectable,
    customRowhook,
    type,
    selectableRows,
    disableSpecificRowFn,
    handleSelectState,
    resetValue,
    pagination?.limit,
    pagination?.total,
  ]);

  // react-table plugins augment the instance with selectedFlatRows, toggleRowSelected, etc.,
  // but @types/react-table doesn't infer those from the runtime hooks. Cast to any for ergonomic access.
  const tableInstance: any = useTable(
    finalTableParams as any,
    ...allTableHooks
  );

  useEffect(() => {
    if (getTableState && (enableExpanded || isColumnLevelSorting)) {
      getTableState(tableInstance.state);
    }
  }, [
    getTableState,
    tableInstance.state,
    enableExpanded,
    isColumnLevelSorting,
  ]);

  const {
    selectedFlatRows,
    toggleRowSelected,
    toggleAllRowsSelected,
    setColumnOrder,
    allColumns,
    toggleHideColumn,
    setHiddenColumns,
  } = tableInstance;

  useEffect(() => {
    if (selectionState === 'all') {
      const {
        rows,
        state: { selectedRowIds },
      } = tableInstance;
      const currentPage = page?.number;

      if (prevPage === currentPage) {
        const unselectedRowIds = rows.filter(({ id }) => !selectedRowIds[id]);
        const newUnSelectedHash = { ...unSelectedHash };
        newUnSelectedHash[currentPage] = unselectedRowIds;
        setUnSelectedHash(newUnSelectedHash);
      } else if (
        !unSelectedHash[currentPage] ||
        !unSelectedHash[currentPage].length
      ) {
        toggleAllRowsSelected(true);
      } else if (unSelectedHash[currentPage].length) {
        const unSelectedUsers = unSelectedHash[currentPage];

        toggleAllRowsSelected(true);
        unSelectedUsers.forEach((user) => {
          toggleRowSelected(user.id, false);
        });
      }

      setPrevPage(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionState, tableInstance.selectedFlatRows]);

  // Expose clear selection so parent can call when bulk operation completes (no useEffect)
  if (clearSelectionRef && isRowSelectable) {
    clearSelectionRef.current = () => {
      resetValue();
      toggleAllRowsSelected(false);
    };
  }

  const totalCount = pagination?.total;
  const unselectedUserIds =
    selectionState === 'all'
      ? Object.values(unSelectedHash)
          ?.flat()
          ?.map((user) => user.original.id)
      : null;
  const isBulkActionsEnabled =
    (unselectedUserIds && totalCount - unselectedUserIds?.length > 0) ||
    selectedFlatRows?.length > 0;

  // Notify BulkOperationsContext when bulk actions bar visibility changes
  // This hides the GlobalBulkOperationQueue when table bulk actions are visible
  const bulkOpsContext = useBulkOperationsOptional();
  useEffect(() => {
    if (bulkOpsContext?.setBulkActionsVisible) {
      bulkOpsContext.setBulkActionsVisible(isBulkActionsEnabled);
    }
    // Cleanup: set to false when component unmounts
    return () => {
      if (bulkOpsContext?.setBulkActionsVisible) {
        bulkOpsContext.setBulkActionsVisible(false);
      }
    };
  }, [isBulkActionsEnabled, bulkOpsContext]);

  return (
    <Flex
      gap="10px"
      w="100%"
      h="100%"
      flexDir="column"
      overflow="hidden"
      data-test-id={testID}
      display={toHide ? 'none' : 'flex'}
      position="relative"
      {...tableContainerStyles}
    >
      <Flex
        flexDir="column"
        overflow="auto"
        flex="1"
        {...tableLayoutWrapperStyles}
      >
        {enableColumnConfig && (
          <TableColumnSettings
            {...({
              tableId,
              isRowSelectable,
              setColumnOrder,
              setHiddenColumns,
              toggleHideColumn,
              tableColStyles,
              columns,
              allColumns: allColumns.slice(
                isRowSelectable ? 1 : 0,
                enableRowAction ? -1 : allColumns.length
              ),
              addDefaultColumnsAtTop: addDefaultColumnAtTop,
              onTableColDataChange,
              initialTableColData,
              skipLocalStorage,
            } as any)}
          />
        )}

        <TableLayout
          {...({
            layoutStyles,
            tableLayoutStyles,
            tableStyles: layoutTableStyles,
            tableInstance,
            isRowSelectable,
            isColumnLevelSorting,
            enableRowAction,
            layoutScrollContainerStyles,
            tableRef,
            isLoadingMore,
            showFooter: enableFooter,
            rowStyles,
          } as any)}
        />
      </Flex>

      {!skipPagination && !(isRowSelectable && isBulkActionsEnabled) && (
        <Flex
          bottom="6px"
          right="10px"
          zIndex={10}
          flexShrink="0"
          position="absolute"
          justifyContent="flex-end"
        >
          {enableLoadMore ? (
            <LoadMore
              {...({
                pagination,
                page,
                paginationLimit,
                applyPage,
                isLoading: isLoadingMore,
              } as any)}
            />
          ) : (
            <Pagination
              {...({
                type: paginationType,
                pagination,
                number: page?.number,
                applyPage,
                applyLimit,
                isDispatch,
                enableNthSelection,
                enableLimitSelection,
                limit: paginationLimit,
                totalKey,
              } as any)}
            />
          )}
        </Flex>
      )}
      {isBulkActionsEnabled && (
        <TableBulkActions
          toggleRowSelected={toggleRowSelected}
          toggleAllRowsSelected={toggleAllRowsSelected}
          bulkActionMenuItems={bulkActionMenuItems}
          selected={selectedFlatRows.map((item: any) => ({
            ...item.original,
            tableId: item.id,
            currUser: item?.currUser,
          }))}
          unSelected={unselectedUserIds}
          totalCount={pagination?.total}
          resetSelectionValue={resetValue}
        />
      )}
    </Flex>
  );
};

// Wrap Table in React.memo to prevent unnecessary re-renders
export const Table = React.memo(TableComponent);
